import React, { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';

/**
 * Admin pricing editor (Phase-1 #7). Edits per-plan monthly/annual prices. The
 * override is applied server-side inside get_plan()/get_all_plans(), so the same
 * effective price drives BOTH the public pricing page AND the charged amount —
 * they can never diverge. Plan structure/features remain code-defined; only
 * prices are editable here. "Reset" restores the code default.
 */
const AdminPricingPage: React.FC = () => {
  const {
    sidebarCollapsed, setSidebarCollapsed, activeSection,
    setSelectedRegulation, handleNavClick, handleLogout,
  } = useAdminNavigation();

  const [rows, setRows] = useState<adminApi.AdminPlanPricing[]>([]);
  const [edits, setEdits] = useState<Record<string, { monthly: string; annual: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingTier, setSavingTier] = useState<string | null>(null);
  const [savedTier, setSavedTier] = useState<string | null>(null);

  const token = window.localStorage.getItem('policyora_token');

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    adminApi.getAdminPricing(token)
      .then((d) => {
        setRows(d);
        const e: Record<string, { monthly: string; annual: string }> = {};
        d.forEach((p) => { e[p.tier] = { monthly: p.monthly_price_usd ?? '', annual: p.annual_price_usd ?? '' }; });
        setEdits(e);
      })
      .catch((err) => setError(err.message || 'Failed to load pricing'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (tier: string) => {
    if (!token) return;
    const e = edits[tier];
    const monthly = e.monthly.trim() === '' ? null : Number(e.monthly);
    const annual = e.annual.trim() === '' ? null : Number(e.annual);
    if ((monthly !== null && (isNaN(monthly) || monthly < 0)) || (annual !== null && (isNaN(annual) || annual < 0))) {
      alert('Prices must be non-negative numbers (or blank).');
      return;
    }
    setSavingTier(tier);
    try {
      await adminApi.setAdminPricing(token, tier, { monthly_price_usd: monthly, annual_price_usd: annual });
      setSavedTier(tier);
      setTimeout(() => setSavedTier(null), 1500);
      load();
    } catch (err: any) {
      alert(err.message || 'Save failed');
    } finally {
      setSavingTier(null);
    }
  };

  const reset = async (tier: string) => {
    if (!token) return;
    if (!window.confirm(`Reset ${tier} pricing to the code default?`)) return;
    setSavingTier(tier);
    try {
      await adminApi.resetAdminPricing(token, tier);
      load();
    } catch (err: any) {
      alert(err.message || 'Reset failed');
    } finally {
      setSavingTier(null);
    }
  };

  const toggleDisabled = async (tier: string, currentlyDisabled: boolean) => {
    if (!token) return;
    const next = !currentlyDisabled;
    if (next && !window.confirm(`Deactivate "${tier}"? It will be hidden from the pricing page and blocked for new subscriptions. Existing subscribers are unaffected.`)) return;
    setSavingTier(tier);
    try {
      await adminApi.setAdminPricing(token, tier, { disabled: next });
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to change tier status');
    } finally {
      setSavingTier(null);
    }
  };

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.6rem 0.8rem', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)', borderBottom: '1px solid var(--ds-border)', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '0.6rem 0.8rem', fontSize: '0.85rem', color: 'var(--ds-text)', borderBottom: '1px solid var(--ds-surface-2)', whiteSpace: 'nowrap' };
  const input: React.CSSProperties = { width: 90, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--ds-border-strong)', fontSize: '0.85rem' };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel="Pricing"
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section style={{ padding: '1.25rem 1.5rem' }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--ds-text)' }}>Pricing</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--ds-text-muted)', fontSize: '0.88rem' }}>
            Edit plan prices. Changes apply to both the public pricing page and what customers are charged. Leave blank for contact-sales tiers. "Reset" restores the built-in default.
          </p>
        </div>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--ds-text-muted)' }}>Loading pricing…</div>
        ) : error ? (
          <div style={{ padding: '1rem', borderRadius: 8, background: 'rgba(240,68,56,0.08)', color: 'var(--ds-danger-text)' }}>
            {error} <button type="button" onClick={load} style={{ marginLeft: 8, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto', border: '1px solid var(--ds-border)', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={th}>Plan</th>
                  <th style={th}>Monthly ($)</th>
                  <th style={th}>Annual ($)</th>
                  <th style={th}>Annual /mo</th>
                  <th style={th}>Save %</th>
                  <th style={th}>Source</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const e = edits[p.tier] || { monthly: '', annual: '' };
                  return (
                    <tr key={p.tier}>
                      <td style={td}><strong>{p.display_name}</strong><br /><span style={{ color: 'var(--ds-text-muted)', fontSize: '0.75rem' }}>{p.tier}</span></td>
                      <td style={td}>
                        <input style={input} type="number" min="0" step="1" value={e.monthly}
                          placeholder={p.contact_sales ? 'custom' : '—'}
                          onChange={(ev) => setEdits((s) => ({ ...s, [p.tier]: { ...e, monthly: ev.target.value } }))} />
                      </td>
                      <td style={td}>
                        <input style={input} type="number" min="0" step="1" value={e.annual}
                          placeholder={p.contact_sales ? 'custom' : '—'}
                          onChange={(ev) => setEdits((s) => ({ ...s, [p.tier]: { ...e, annual: ev.target.value } }))} />
                      </td>
                      <td style={td}>{p.annual_monthly_equivalent ?? '—'}</td>
                      <td style={td}>{p.annual_savings_pct ? `${p.annual_savings_pct}%` : '—'}</td>
                      <td style={td}>
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: p.overridden ? 'rgba(31,94,255,0.1)' : 'var(--ds-surface-2)', color: p.overridden ? 'var(--ds-brand-blue)' : 'var(--ds-text-muted)' }}>
                          {p.overridden ? 'Custom' : 'Default'}
                        </span>
                        {p.disabled && (
                          <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'rgba(217,45,32,0.1)', color: 'var(--ds-danger-text)' }}>
                            Deactivated
                          </span>
                        )}
                      </td>
                      <td style={td}>
                        <button type="button" disabled={savingTier === p.tier} onClick={() => save(p.tier)}
                          style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', marginRight: 6, opacity: savingTier === p.tier ? 0.6 : 1 }}>
                          {savingTier === p.tier ? '…' : savedTier === p.tier ? 'Saved ✓' : 'Save'}
                        </button>
                        <button type="button" disabled={savingTier === p.tier} onClick={() => toggleDisabled(p.tier, p.disabled)}
                          style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${p.disabled ? 'var(--ds-success)' : 'var(--ds-danger)'}`, background: 'transparent', color: p.disabled ? 'var(--ds-success)' : 'var(--ds-danger)', fontSize: '0.8rem', cursor: 'pointer', marginRight: 6 }}>
                          {p.disabled ? 'Activate' : 'Deactivate'}
                        </button>
                        {p.overridden && (
                          <button type="button" disabled={savingTier === p.tier} onClick={() => reset(p.tier)}
                            style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--ds-border-strong)', background: 'transparent', color: 'var(--ds-text-secondary)', fontSize: '0.8rem', cursor: 'pointer' }}>
                            Reset
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </DashboardLayout>
  );
};

export default AdminPricingPage;
